import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Wrench, Wind, Thermometer, Droplets, Layers, Zap, ArrowRight, Check } from "lucide-react";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

// Technologies installées (sourced brief FLOW_OK)
const technologies = [
  { icon: Wrench,      title: "Gainables",                    desc: "Systèmes gainables pour une diffusion discrète et homogène." },
  { icon: Layers,      title: "VRV / VRF",                    desc: "Systèmes à débit de réfrigérant variable pour grandes surfaces et zones multiples." },
  { icon: Thermometer, title: "Mono-split & multi-split",     desc: "Solutions split adaptées aux pièces uniques ou à plusieurs espaces." },
  { icon: Zap,         title: "PAC & thermodynamique",        desc: "Pompes à chaleur et systèmes thermodynamiques pour chauffage et rafraîchissement." },
  { icon: Droplets,    title: "Eau glacée",                   desc: "Installations eau glacée pour les besoins de forte puissance." },
  { icon: Wind,        title: "Double flux & récupération",   desc: "Ventilation double flux et récupération d'énergie pour une qualité d'air maîtrisée." }
];

const brands = ["Daikin", "Mitsubishi", "Hitachi", "Bosch"];
const badges = ["Techniciens certifiés", "Garantie décennale", "SAV 7j/7", "Devis gratuit"];

export default function InstallationPage() {
  return (
    <>
      <Helmet>
        <title>Installation climatisation Paris | FLOW</title>
        <meta name="description" content="Installation de systèmes de climatisation et ventilation par FLOW : gainable, VRV/VRF, mono-split, multi-split, PAC, eau glacée, récupération d'énergie, thermodynamique, double flux. BET fluide intégré. Marques Daikin, Mitsubishi, Hitachi, Bosch." />
        <link rel="canonical" href="https://flow-air.com/installation" />
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#F5F7FA] to-white py-16 md:py-24" data-testid="installation-hero">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto">
            <span className="caption mb-4 block">Service — Installation</span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0E1A2B] mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Installation de systèmes de <span className="text-[#00AFFF]">climatisation et ventilation</span>
            </h1>
            <p className="text-lg text-[#475569] leading-relaxed">
              FLOW installe une large gamme de systèmes HVAC : gainable, VRV/VRF, mono-split,
              multi-split, pompe à chaleur, eau glacée, récupération d'énergie, thermodynamique
              et double flux. BET fluide intégré.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Technologies installées */}
      <section className="py-16 md:py-24 bg-white" data-testid="installation-technologies">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center mb-12 max-w-2xl mx-auto">
            <span className="caption mb-4 block">Technologies</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0E1A2B]" style={{ fontFamily: 'Sora, sans-serif' }}>
              Les systèmes que nous installons
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {technologies.map((tech, index) => {
              const Icon = tech.icon;
              return (
                <motion.div
                  key={tech.title}
                  className="p-6 bg-[#F5F7FA] rounded-2xl border border-transparent hover:border-[#00AFFF]/30 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  data-testid={`installation-tech-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00AFFF]/15 to-[#00AFFF]/5 flex items-center justify-center mb-4">
                    <Icon size={22} className="text-[#00AFFF]" />
                  </div>
                  <h3 className="text-base font-semibold mb-2 text-[#0E1A2B]">{tech.title}</h3>
                  <p className="text-[#475569] text-sm leading-relaxed">{tech.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BET fluide intégré */}
      <section className="py-16 md:py-24 bg-[#F5F7FA]" data-testid="installation-bet">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="max-w-3xl mx-auto text-center">
            <span className="caption mb-4 block">Bureau d'études</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0E1A2B] mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              BET fluide intégré
            </h2>
            <p className="text-[#475569] leading-relaxed text-base md:text-lg">
              FLOW intègre son propre bureau d'études fluide pour dimensionner, concevoir
              et coordonner les installations CVC. Une seule équipe, de l'étude à la mise
              en service, pour garantir la cohérence technique de chaque projet.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Marques */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center">
            <span className="caption mb-6 block">Marques installées</span>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
              {brands.map((brand) => (
                <span
                  key={brand}
                  className="text-lg font-bold tracking-wider text-[#94A3B8] hover:text-[#0E1A2B] transition-colors"
                >
                  {brand.toUpperCase()}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Badges */}
      <section className="py-12 bg-[#F5F7FA]">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
            {badges.map((item, i) => (
              <div key={i} className="flex items-center justify-center gap-2 py-3 px-4 bg-white rounded-lg shadow-sm">
                <div className="w-6 h-6 rounded-md bg-gradient-to-br from-[#00AFFF]/15 to-[#00AFFF]/5 flex items-center justify-center">
                  <Check size={12} className="text-[#00AFFF]" />
                </div>
                <span className="text-xs font-medium text-[#0E1A2B]">{item}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[#0E1A2B]">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Parlons de votre projet
            </h2>
            <p className="text-[#94A3B8] mb-8 leading-relaxed">
              Contactez l'équipe FLOW pour échanger sur votre besoin en installation HVAC.
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="installation-cta-contact">
              Demander un devis <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
